import type {
  WorkflowStepDef,
  WorkflowSystemStep,
  WorkflowView,
} from '@/lib/api/generated/model';

/**
 * Every position a task can occupy in one workflow: the synthesized start
 * step, every phase-declared step, and the synthesized done step.
 *
 * The done/start steps matter here, not just phase steps: the backend
 * contract's own example config (`contracts/trigger-profiles-api.md`) uses
 * `to_step_id: "done"` — a continue-task action's target step is explicitly
 * allowed to be a workflow's terminal step (spec FR-003, backend FR-011,
 * "including when that step is a terminal step of the task's workflow"),
 * and that step is a `WorkflowSystemStep`, never a member of any phase
 * (`WorkflowSystemStep`'s own doc comment). A picker (or a display label)
 * built only from `phases.flatMap(p => p.steps)` could never reach it.
 *
 * Shared by `WorkflowStepSelect` (picking a step) and `TriggerProfileList`/
 * `TriggerProfileDetailSheet` (labeling a stored step id) — one definition,
 * so the two never resolve a step id to a different name.
 */
export function allWorkflowSteps(
  workflow: WorkflowView,
): (WorkflowStepDef | WorkflowSystemStep)[] {
  return [
    workflow.start_step,
    ...workflow.phases.flatMap((phase) => phase.steps ?? []),
    workflow.done_step,
  ];
}

/** The step's display name, or the raw id if the workflow no longer has a
 *  step by that id (Edge Cases: a workflow changed after a profile was
 *  configured against it — the stored id is still shown, not hidden). */
export function resolveStepName(
  workflow: WorkflowView | undefined,
  stepId: string,
): string {
  if (!workflow) return stepId;
  return (
    allWorkflowSteps(workflow).find((step) => step.id === stepId)?.name ??
    stepId
  );
}
